import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import axios from '../../config/axiosConfig';
import owaspWstgService from '../../services/owaspWstgService';

const STATUS_COLORS = {
  pass: { dark: 'bg-green-800/60 border-green-700 text-green-300', light: 'bg-green-100 border-green-300 text-green-800' },
  fail: { dark: 'bg-red-800/60 border-red-700 text-red-300', light: 'bg-red-100 border-red-300 text-red-800' },
  info: { dark: 'bg-blue-800/60 border-blue-700 text-blue-300', light: 'bg-blue-100 border-blue-300 text-blue-800' },
  not_tested: { dark: 'bg-gray-800/40 border-gray-700 text-gray-500', light: 'bg-gray-100 border-gray-200 text-gray-400' }
};

const WstgHeatmap = ({ webTarget, isDarkMode, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredTest, setHoveredTest] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (webTarget?._id) loadData();
  }, [webTarget]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/web-reports/wstg/${webTarget._id}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load WSTG heatmap:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseEnter = (test, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredTest(test);
    setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 8 });
  };

  if (loading || !data) {
    return <div className={`flex items-center justify-center p-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}><FontAwesomeIcon icon={faSync} spin className="mr-2" /> Loading heatmap...</div>;
  }

  // Group by category
  const grouped = {};
  for (const c of data.coverage) {
    if (!grouped[c.categoryCode]) grouped[c.categoryCode] = [];
    grouped[c.categoryCode].push(c);
  }

  const catCodes = Object.keys(grouped).sort();

  if (compact) {
    // Compact inline view for overlays
    return (
      <div className="flex flex-wrap gap-0.5">
        {data.coverage.map(test => {
          const sc = STATUS_COLORS[test.status];
          const cls = isDarkMode ? sc.dark : sc.light;
          return (
            <div key={test.testId} className={`w-3 h-3 rounded-sm border ${cls}`} title={`${test.testId}: ${test.testName} (${test.status})`} />
          );
        })}
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3 relative">
      {/* Summary */}
      <div className="flex items-center gap-4">
        <span className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          WSTG Coverage: {data.summary.coveragePercent}%
        </span>
        <div className="flex gap-2 text-[9px]">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-500" /> Pass ({data.summary.pass})</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500" /> Fail ({data.summary.fail})</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500" /> Info ({data.summary.info})</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-500" /> Untested ({data.summary.notTested})</span>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-12 gap-1">
        {catCodes.map(code => {
          const config = owaspWstgService.getCategoryConfig(code);
          const tests = grouped[code] || [];
          return (
            <div key={code} className="space-y-0.5">
              <div className={`text-[7px] font-bold text-center truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} title={config.name}>
                {code.replace('WSTG-', '')}
              </div>
              <div className={`text-[6px] text-center truncate ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                ({tests.length})
              </div>
              {tests.map(test => {
                const sc = STATUS_COLORS[test.status];
                const cls = isDarkMode ? sc.dark : sc.light;
                return (
                  <div
                    key={test.testId}
                    className={`h-4 rounded-sm border cursor-pointer text-[6px] flex items-center justify-center ${cls} hover:ring-1 hover:ring-teal-400`}
                    onMouseEnter={e => handleMouseEnter(test, e)}
                    onMouseLeave={() => setHoveredTest(null)}
                    title={`${test.testId}: ${test.testName}`}
                  >
                    {test.testId.split('-').pop()}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Tooltip */}
      {hoveredTest && (
        <div
          className={`fixed z-50 px-3 py-2 rounded shadow-lg text-xs max-w-xs pointer-events-none ${isDarkMode ? 'bg-gray-700 text-gray-200 border border-gray-600' : 'bg-white text-gray-800 border border-gray-200'}`}
          style={{ left: tooltipPos.x, top: tooltipPos.y, transform: 'translate(-50%, -100%)' }}
        >
          <div className="font-bold">{hoveredTest.testId}</div>
          <div>{hoveredTest.testName}</div>
          <div className={`mt-1 ${STATUS_COLORS[hoveredTest.status][isDarkMode ? 'dark' : 'light'].split(' ').find(c => c.startsWith('text-'))}`}>
            Status: {hoveredTest.status}
          </div>
          {hoveredTest.findingCount > 0 && <div className="text-red-400">{hoveredTest.findingCount} finding(s)</div>}
          {hoveredTest.notes && <div className="text-gray-400 mt-1">{hoveredTest.notes}</div>}
          {hoveredTest.mitreMapping?.length > 0 && (
            <div className="text-purple-400 mt-1">ATT&CK: {hoveredTest.mitreMapping.map(m => m.techniqueId).join(', ')}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default WstgHeatmap;
